import { useMemo, useState, useCallback, useRef } from 'react';
import FileEnlarged from './FileEnlarged';
import { AnimatePresence } from 'motion/react';
import styles from './TrackerCarousel.module.css';

export default function TrackerCarousel({files}) {

    const [ enlarged, setEnlarged ] = useState({open: false, src: ''});
    const carouselRef = useRef(null);
    const fileLength = files.length || 0;

    const handleFileClick = useCallback((file) => {
        const fileType = getFileType(file);
        if (isImageType(fileType)) {
            setEnlarged({open: true, src: file.url})
        }
    },[]);

    const handleCloseEnlarged = () => {
        setEnlarged({open: false, src: ''})
    };

    const scrollCarousel = (direction) => {
        if (carouselRef.current) {
            const fileWidth = carouselRef.current.querySelector(`.${styles.file}`).offsetWidth + 16;
            const scrollDistance = direction === 'next' ? fileWidth : -fileWidth;

            carouselRef.current.scrollBy({
                left: scrollDistance,
                behavior: 'smooth',
            });
        }
    };

    return (
        <>
        <div className={styles.carouselContainer}>
            <div className={styles.carousel} ref={carouselRef}>
                {fileLength > 0 ? (
                    files.map((file, index) => (
                        <div key={index} className={styles.file}>
                            <FileContent file={file} handleFileClick={handleFileClick} />
                        </div>
                    ))
                ) : (
                    <div className={styles.noFiles}>There are no files associated with this QR.</div>
                )}
                <button className={styles.swipeBtn} onClick={() => scrollCarousel('prev')} disabled={fileLength <= 3}><i className='far fa-chevron-left' /></button>
                <button className={styles.swipeBtn} onClick={() => scrollCarousel('next')}  disabled={fileLength <= 3}><i className='far fa-chevron-right' /></button>
            </div>
        </div>
        <AnimatePresence initial={false}>
            {enlarged.open && <FileEnlarged src={enlarged.src} onClose={handleCloseEnlarged} />}
        </AnimatePresence>
        </>
    );
};

const FileContent = ({file, handleFileClick}) => {
    const handleFileReturn = useMemo(() => {
        const fileType = getFileType(file);
        if (isImageType(fileType)) {
            return (
                <div className={styles.imgWrap} onClick={() => handleFileClick(file)}>
                    <img src={file?.url} className={styles.img} alt={`Liquid Label file`} />
                </div>
            );
        } else if (fileType === 'pdf') {
            return (
                <div className={styles.pdfWrap}>
                    <i className='far fa-file-pdf' />
                    <a href={file?.url} className={styles.pdfBtn} download={`${file?.path.split('/')[1]}`} target='_blank'>Download</a>
                </div>
            );
        }
        return null;
    },[file, handleFileClick]);

    return <>{handleFileReturn}</>
};

const getFileTypeFromUrl = (urlString) => {
    if (!urlString) return null;
    const url = new URL(urlString, window.location.origin);
    const pathname = url.pathname;
    const lastDotIndex = pathname.lastIndexOf('.');

    if (lastDotIndex === -1 || lastDotIndex === pathname.length - 1) {
        return null; // no extension
    }

    const extension = pathname.substring(lastDotIndex + 1).toLowerCase();

    return extension;
};

const getFileType = (file) => {
    const fromUrl = getFileTypeFromUrl(file?.url);
    if (fromUrl) return fromUrl;

    if (file?.file?.type?.startsWith('image/')) {
        return file.file.type.split('/')[1];
    }

    if (file?.file?.type === 'application/pdf') {
        return 'pdf';
    }

    if (file?.name?.includes('.')) {
        return file.name.split('.').pop().toLowerCase();
    }

    return null;
};

const isImageType = (fileType) => ['webp', 'png', 'jpg', 'jpeg', 'gif'].includes(fileType);
